import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { AuthService } from './auth.service';
import { LoggerService } from '../services/logger.service';

/**
 * Functional route guard that checks authentication and role-based authorization.
 *
 * Usage in route config:
 *   canActivate: [authGuard]
 *
 * Reads `roles` from route data:
 *   - `['*']` (default) — any authenticated user
 *   - `['ShiftAdmin']` — user must have the named role
 *
 * Redirects unauthenticated users to `/login`.
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  const logError = logger.getLogFn('AuthGuard', 'error');

  const roles: string[] = route.data['roles'] ?? ['*'];

  // Wildcard — any authenticated user is allowed
  if (roles[0] === '*') {
    return true;
  }

  const currentUser = authService.getCurrentUser();

  if (currentUser) {
    if (!route.data['allowAnonymous']) {
      const userRoleNames = currentUser.roles?.map((r) => r.name) ?? [];
      const hasRequiredRole = roles.some((role) => userRoleNames.includes(role));

      if (!hasRequiredRole) {
        console.warn('Not authorized to access [' + route.url + ']');
        logError('Not authorized to access [' + route.url + ']', undefined, 'AuthGuard');
        return false;
      }
    }
    return true;
  } else {
    void router.navigateByUrl('/login');
    return false;
  }
};

/**
 * Functional child-route guard that verifies the user is authenticated.
 *
 * Usage in route config:
 *   canActivateChild: [authGuardChild]
 */
export const authGuardChild: CanActivateChildFn = (_route: ActivatedRouteSnapshot): boolean => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const authData = authService.getAuthData();
  if (authData) {
    return true;
  } else {
    void router.navigateByUrl('/login');
    return false;
  }
};
